import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';
import React, { useCallback, useMemo, useState } from 'react';
import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { v, s } from '../../../utils/libs';
import { theme } from '../../../theme';
import {
  shouldUseExpandedLayout,
  shouldUseWebLayout,
  shouldUseWidthConstraint,
} from '../../../utils/layoutUtils';
import { applyExpandedLayoutSnapBoost } from '../../../utils/bottomSheetSnapPoints';
import SheetBackGuard from './SheetBackGuard';

interface CustomBottomSheetProps {
  bottomSheetModalRef: React.RefObject<BottomSheetModal | null>;
  snapPoints: string[];
  /** The snap point the sheet opens at (default: the lowest). */
  index?: number;
  enableDynamicSizing?: boolean;
  maxDynamicContentSize?: number;
  handleSheetChanges: (index: number) => void;
  /**
   * Called once the sheet is really closed (×, swipe down, backdrop), unlike
   * `handleSheetChanges(-1)`, which also fires while the sheet is hidden
   * behind another sheet opened on top of it.
   */
  onDismiss?: () => void;
  children?: React.ReactNode;
  // Optional fixed areas rendered outside the scrollable content
  header?: React.ReactNode;
  footer?: React.ReactNode;
  // When true, do not wrap children with BottomSheetScrollView
  disableDefaultScroll?: boolean;
  /**
   * Space after the scrollable content. Defaults to room for a footer; pass 0
   * for a sheet without one so content that fits does not scroll.
   */
  scrollBottomPadding?: number;
  // When true, constrains width (maxWidth: WEB_MAX_CONTENT_WIDTH) on web and native tablet landscape
  constrainWidth?: boolean;
  /** When true, skip the 20% expanded-layout snap boost (default: boost when shouldUseExpandedLayout). */
  disableSnapBoost?: boolean;
  /**
   * False keeps the sheet up while something is in flight: swipe-down and
   * the backdrop no longer close it. The × stays the caller's to disable.
   */
  dismissible?: boolean;
}
const CustomBottomSheet = ({
  bottomSheetModalRef,
  snapPoints,
  index = 0,
  enableDynamicSizing,
  maxDynamicContentSize,
  handleSheetChanges,
  onDismiss,
  children,
  header,
  footer,
  disableDefaultScroll,
  scrollBottomPadding,
  constrainWidth,
  disableSnapBoost,
  dismissible = true,
}: CustomBottomSheetProps) => {
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const applyConstrainedWidth = useMemo(
    () =>
      Boolean(
        shouldUseWidthConstraint(width, height) && (constrainWidth ?? true),
      ),
    [constrainWidth, width, height],
  );

  const resolvedSnapPoints = useMemo(() => {
    if (disableSnapBoost || !shouldUseExpandedLayout(width, height)) {
      return snapPoints;
    }
    return applyExpandedLayoutSnapBoost(snapPoints);
  }, [snapPoints, width, height, disableSnapBoost]);

  // Whether the sheet is up and on top, so back closes it first. A sheet
  // hidden behind one opened on top of it reports -1, so back goes to the top.
  const [isOpen, setIsOpen] = useState(false);
  const handleChange = useCallback(
    (index: number) => {
      setIsOpen(index >= 0);
      handleSheetChanges(index);
    },
    [handleSheetChanges],
  );
  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    onDismiss?.();
  }, [onDismiss]);
  const dismissSheet = useCallback(
    () => bottomSheetModalRef.current?.dismiss(),
    [bottomSheetModalRef],
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => {
      // Use custom backdrop on web to avoid gesture-handler worklet warning
      if (Platform.OS === 'web') {
        const { animatedIndex, style } = props;
        const animatedStyle = useAnimatedStyle(() => ({
          opacity: interpolate(animatedIndex.value, [-1, 0], [0, 0.5]),
          display: animatedIndex.value < -0.5 ? 'none' : 'flex',
        }));
        return (
          <Animated.View
            style={[style, { backgroundColor: '#000' }, animatedStyle]}>
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={() => {
                if (dismissible) bottomSheetModalRef.current?.dismiss();
              }}
            />
          </Animated.View>
        );
      }
      return (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          pressBehavior={dismissible ? 'close' : 'none'}
        />
      );
    },
    [bottomSheetModalRef, dismissible],
  );

  const renderFooter = useCallback(
    (props: BottomSheetFooterProps) => (
      <BottomSheetFooter {...props}>
        <View
          style={[
            styles.footerContainer,
            { paddingBottom: v(16) },
            applyConstrainedWidth && styles.webFooterContainer,
          ]}>
          {footer}
        </View>
      </BottomSheetFooter>
    ),
    [footer, insets.bottom, applyConstrainedWidth],
  );

  return (
    <>
      <SheetBackGuard
        open={isOpen}
        dismissible={dismissible}
        dismiss={dismissSheet}
      />
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={index}
        bottomInset={insets.bottom}
        animateOnMount={true}
        enableDynamicSizing={enableDynamicSizing}
        maxDynamicContentSize={maxDynamicContentSize}
        snapPoints={resolvedSnapPoints}
        onChange={handleChange}
        onDismiss={handleDismiss}
        enablePanDownToClose={dismissible}
        enableOverDrag={false}
        android_keyboardInputMode="adjustPan"
        keyboardBehavior="extend"
        footerComponent={renderFooter}
        keyboardBlurBehavior="restore"
        backdropComponent={renderBackdrop}>
        <View style={styles.container}>
          {header ? (
            <View
              style={[
                styles.headerContainer,
                applyConstrainedWidth && styles.webContentWrapper,
              ]}>
              {header}
            </View>
          ) : null}

          <View
            style={[
              styles.contentWrapper,
              applyConstrainedWidth && styles.webContentWrapper,
            ]}>
            {disableDefaultScroll ? (
              <View style={styles.content}>{children}</View>
            ) : (
              <BottomSheetScrollView
                keyboardShouldPersistTaps="handled"
                style={styles.content}
                contentContainerStyle={[
                  styles.contentContainer,
                  scrollBottomPadding !== undefined && {
                    paddingBottom: scrollBottomPadding,
                  },
                ]}>
                {children}
              </BottomSheetScrollView>
            )}
          </View>
        </View>
      </BottomSheetModal>
    </>
  );
};

export default CustomBottomSheet;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webContentWrapper: {
    maxWidth: WEB_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: s(24),
  },
  contentWrapper: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    // extra space so scrollable content doesn't butt up against the footer
    paddingBottom: v(72),
  },
  headerContainer: {
    // justifyContent: 'center',
    // alignItems: 'center',
  },
  footerContainer: {
    backgroundColor: theme.colors.surface,
    paddingBottom: v(16),
    marginHorizontal: s(2),
  },
  webFooterContainer: {
    maxWidth: WEB_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: s(24),
  },
});
